import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { scansAPI, utilsAPI, scheduledScansAPI, discoveryScansAPI, statsAPI } from '../services/api';
import { markOnboardingStepComplete } from '../services/onboardingSync';
import { useTenantContext } from '../context/TenantContext';
import socketService from '../services/socket';
import { formatDuration } from '../utils/format';
import ScanWizard from '../components/ScanWizard';
import ReturnBanner from '../components/ReturnBanner';
import './Scans.css';

const SCAN_TYPE_INFO = {
  full: {
    title: 'Full Scan',
    description: 'A comprehensive non-intrusive analysis combining Static Code Analysis (SAST), Taint Tracking, Dependency Scanning (SCA), Configuration Audits, and Network Traffic analysis.',
    details: [
      'SAST: Finds logical flaws and hardcoded secrets in source code.',
      'Taint Tracking: Maps data paths from untrusted sources to sensitive sinks.',
      'SCA: Identifies vulnerable 3rd-party libraries using the OSV database.',
      'Config: Ensures environment settings and IaC follow best practices.',
      'Network: Advanced analysis for insecure protocols, anomalies, and DNS exfiltration.'
    ]
  },
  code: {
    title: 'Code Analysis',
    description: 'Focuses on the application source code using SAST and Taint Tracking.',
    details: [
      'Identifies logical vulnerabilities (e.g., Command Injection, XSS).',
      'Detects hardcoded API keys, passwords, and tokens.',
      'Analyzes Abstract Syntax Trees (AST) for Python logic flaws.',
      'Traces unsanitized data flow from request parameters to database queries.'
    ]
  },
  config: {
    title: 'Configuration Analysis',
    description: 'Inspects infrastructure settings, environment variables, and deployment configs.',
    details: [
      'Flags insecure CORS, SSL/TLS, and Debug settings.',
      'Checks for exposed credentials in .env and config files.',
      'Audits Docker and environment configurations for best practices.'
    ]
  },
  traffic: {
    title: 'Network Traffic Analysis',
    description: 'Deep passive analysis of network logs and PCAP files for advanced security risks.',
    details: [
      'Detects insecure protocols (HTTP, FTP, SMTP, etc.) and legacy TLS (SSL 3.0, TLS 1.0/1.1).',
      'Flags plaintext credentials, bearer tokens, and JWTs in packet payloads.',
      'Advanced DNS security: Detects exfiltration, tunneling, and NXDOMAIN bursts.',
      'Flow Anomaly Detection: Identifies port scans, brute-force attempts, and C2 beaconing.',
      'Protocol-Aware Parsing: Verb-level analysis for FTP, SMTP, and SMBv1.'
    ]
  },
  external_discovery: {
    title: 'External Discovery',
    description: 'Maps internet-facing domains, services, and exposure details without requiring agents.',
    details: [
      'Enumerates subdomains and DNS records.',
      'Identifies exposed IPs and open services.',
      'Collects TLS and HTTP header metadata for exposure baselines.',
      'Tracks first-seen and last-seen timestamps.'
    ]
  }
};

function Scans() {
  const [scans, setScans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [filters, setFilters] = useState({
    severity: '',
  });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSentinel = user.role === 'sentinel';
  const isTrial = user.role === 'trial';
  const canScheduleScans = user.role === 'overdrive' || user.role === 'nexus' || user.role === 'admin';
  console.log("Scans component initialized", { username: user.username, role: user.role, isSentinel });

  // Scheduled Scans State
  const [scheduledScans, setScheduledScans] = useState([]);
  const [scheduledScansLimits, setScheduledScansLimits] = useState({ max_scheduled_scans: 0, current_count: 0 });
  const [showScheduledForm, setShowScheduledForm] = useState(false);
  const [scheduledFormData, setScheduledFormData] = useState({
    name: '',
    target_path: '',
    scan_type: 'full',
    scan_location: 'agent',
    frequency: 'daily',
    day_of_week: 0,
    day_of_month: 1,
    time_of_day: '02:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    email_alerts_enabled: true,
    alert_on_severity: 'high'
  });
  const [editingScheduledScan, setEditingScheduledScan] = useState(null);
  const [scheduledScansLoading, setScheduledScansLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { tenants, selectedTenantId } = useTenantContext();

  const [formData, setFormData] = useState({
    name: '',
    scan_mode: 'internal',
    target_path: '',
    scan_type: 'full',
    scan_location: isSentinel ? 'agent' : 'hub',
    scan_source: 'filesystem', // 'filesystem' or 'repository'
    repo_username: '',
    repo_token: '',
    is_private_repo: false,
    external_domain: '',
    tenant_id: ''
  });

  // Folder Picker State
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPath, setPickerPath] = useState('/app/projects');
  const [pickerItems, setPickerItems] = useState({ current: '/app/projects', parent: null, directories: [], files: [], is_agent: false });
  const [pickerLoading, setPickerLoading] = useState(false);
  const [browseTarget, setBrowseTarget] = useState(isSentinel ? 'agent' : 'hub'); // Default to agent if Sentinel
  const [pickerMode, setPickerMode] = useState('new_scan'); // 'new_scan' or 'scheduled_scan'

  useEffect(() => {
    loadScans();
    loadStats();
    if (canScheduleScans) {
      loadScheduledScans();
    }
  }, [selectedTenantId]);

  useEffect(() => {
    // Listen for real-time scan updates
    socketService.connect();
    socketService.on('scan_progress', (data) => {
      setScans(prevScans => prevScans.map(scan => 
        scan.id === data.scan_id ? { ...scan, status: 'running' } : scan
      ));
    });

    socketService.on('scan_completed', (data) => {
      setScans(prevScans => prevScans.map(scan => 
        scan.id === data.scan_id ? { 
          ...scan, 
          status: 'completed',
          duration: data.duration,
          total_findings: data.total_findings,
          high_severity: data.high_severity,
          medium_severity: data.medium_severity,
          low_severity: data.low_severity
        } : scan
      ));
    });

    socketService.on('scan_failed', (data) => {
      setScans(prevScans => prevScans.map(scan => 
        scan.id === data.scan_id ? { ...scan, status: 'failed' } : scan
      ));
    });

    return () => {
      socketService.off('scan_progress');
      socketService.off('scan_completed');
      socketService.off('scan_failed');
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newParam = params.get('new');
    const severityParam = params.get('severity');

    if (newParam === 'true') {
      setShowWizard(true);
    }

    if (severityParam) {
      setFilters(prev => ({ ...prev, severity: severityParam }));
    }

    if (newParam || severityParam) {
      // Clear parameters so they don't persist on refresh
      navigate('/scans', { replace: true });
    }
  }, [location.search, navigate]);

  const loadScans = async () => {
    setLoading(true);
    try {
      const response = await scansAPI.list({ tenant_id: selectedTenantId });
      const scanList = Array.isArray(response.data) ? response.data : 
                       (Array.isArray(response.data?.scans) ? response.data.scans : []);
      setScans(scanList);
      
      // Auto-sync Onboarding: Presence of any scan completes "schedule_scan"
      if (scanList.length > 0) {
        markOnboardingStepComplete('schedule_scan');
      }
    } catch (error) {
      console.error('Error loading scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await statsAPI.get({ tenant_id: selectedTenantId });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadScheduledScans = async () => {
    setScheduledScansLoading(true);
    try {
      const response = await scheduledScansAPI.list();
      setScheduledScans(response.data.scheduled_scans || []);
      setScheduledScansLimits(response.data.limits || { max_scheduled_scans: 0, current_count: 0 });
    } catch (error) {
      console.error('Error loading scheduled scans:', error);
      // 403 means feature not available for this tier
      if (error.response?.status !== 403) {
        console.error('Unexpected error:', error);
      }
    } finally {
      setScheduledScansLoading(false);
    }
  };

  const handleScheduledSubmit = async (e) => {
    e.preventDefault();

    // Check if target_path is a URL (public repository)
    if (scheduledFormData.target_path && (scheduledFormData.target_path.startsWith('http://') || scheduledFormData.target_path.startsWith('https://'))) {
      alert('Hub repository scanning is under maintenance. Please use an Edge Agent.');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingScheduledScan) {
        await scheduledScansAPI.update(editingScheduledScan.id, scheduledFormData);
      } else {
        await scheduledScansAPI.create(scheduledFormData);
      }
      setShowScheduledForm(false);
      setEditingScheduledScan(null);
      resetScheduledForm();
      loadScheduledScans();
    } catch (error) {
      console.error('Error saving scheduled scan:', error);
      alert(error.response?.data?.message || error.response?.data?.error || 'Error saving scheduled scan');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetScheduledForm = () => {
    setScheduledFormData({
      name: '',
      target_path: '',
      scan_type: 'full',
      scan_location: 'agent',
      frequency: 'daily',
      day_of_week: 0,
      day_of_month: 1,
      time_of_day: '02:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      email_alerts_enabled: true,
      alert_on_severity: 'high'
    });
  };

  const handleEditScheduledScan = (scan) => {
    setEditingScheduledScan(scan);
    setScheduledFormData({
      name: scan.name,
      target_path: scan.target_path,
      scan_type: scan.scan_type || 'full',
      scan_location: scan.scan_location || 'agent',
      frequency: scan.frequency,
      day_of_week: scan.day_of_week || 0,
      day_of_month: scan.day_of_month || 1,
      time_of_day: scan.time_of_day || '02:00',
      timezone: scan.timezone || 'UTC',
      email_alerts_enabled: scan.email_alerts_enabled !== false,
      alert_on_severity: scan.alert_on_severity || 'high'
    });
    setShowScheduledForm(true);
  };

  const handleDeleteScheduledScan = async (id) => {
    if (window.confirm('Are you sure you want to delete this scheduled scan?')) {
      try {
        await scheduledScansAPI.delete(id);
        loadScheduledScans();
      } catch (error) {
        console.error('Error deleting scheduled scan:', error);
        alert('Failed to delete scheduled scan');
      }
    }
  };

  const handleRunNow = async (id) => {
    try {
      await scheduledScansAPI.runNow(id);
      alert('Scan queued for immediate execution');
      loadScans(); // Refresh scans list
    } catch (error) {
      console.error('Error running scan now:', error);
      alert(error.response?.data?.error || 'Failed to run scan');
    }
  };

  const handleToggleActive = async (scan) => {
    try {
      await scheduledScansAPI.update(scan.id, { is_active: !scan.is_active });
      loadScheduledScans();
    } catch (error) {
      console.error('Error toggling scan status:', error);
      alert('Failed to update scan status');
    }
  };

  const loadPickerData = async (path, target = browseTarget) => {
    setPickerLoading(true);
    console.log("Fetching directory list", { path, target, isSentinel });
    try {
      const useAgent = target === 'agent' || isSentinel;
      const response = await utilsAPI.ls(path, useAgent);
      console.log("Directory list response received", response.data);
      console.log("Directories:", response.data.directories);
      console.log("Files:", response.data.files);
      // Ensure files array exists, default to empty array if missing
      const pickerData = {
        ...response.data,
        directories: response.data.directories || [],
        files: response.data.files || []
      };
      setPickerItems(pickerData);
      setPickerPath(pickerData.current);
      setBrowseTarget(pickerData.is_agent ? 'agent' : 'hub');
    } catch (error) {
      console.error('Error loading directory:', error);
      const errorMessage = error.response?.data?.error || 'Could not read directory';
      const errorCode = error.response?.data?.error_code;
      
      // Special handling for Sentinel users when no agent is connected
      if (isSentinel && (errorCode === 'NO_AGENT_CONNECTED' || errorMessage.includes('No edge agent connected'))) {
        const shouldNavigate = window.confirm(
          `${errorMessage}\n\nWould you like to go to the Edge Agents page to set one up?`
        );
        if (shouldNavigate) {
          navigate('/agents');
        }
      } else {
        alert(errorMessage);
      }
    } finally {
      setPickerLoading(false);
    }
  };

  const handleBrowse = (locationOverride = null) => {
    // If locationOverride is provided (from Wizard), use it. Otherwise use current formData.scan_location
    const target = (typeof locationOverride === 'string' && (locationOverride === 'agent' || locationOverride === 'hub')) 
      ? locationOverride 
      : formData.scan_location;

    setBrowseTarget(target);
    setShowPicker(true);
    
    const currentPath = target === 'agent' ? scheduledFormData.target_path : formData.target_path;
    const defaultPath = target === 'hub' ? '/app/projects' : '';
    
    loadPickerData(currentPath || defaultPath, target);
  };

  const selectFolder = (dir) => {
    const newPath = pickerItems.current === '/' ? `/${dir}` : `${pickerItems.current}/${dir}`.replace(/\/+/g, '/');
    loadPickerData(newPath);
  };

  const goUp = () => {
    if (pickerItems.parent) {
      loadPickerData(pickerItems.parent);
    }
  };

  const confirmSelection = () => {
    const newPath = pickerItems.current;
    setFormData(prev => ({ ...prev, target_path: newPath, scan_location: browseTarget }));
    setScheduledFormData(prev => ({ ...prev, target_path: newPath, scan_location: browseTarget }));
    setShowPicker(false);
  };

  const buildScanPayload = (data) => {
    if (data.scan_mode === 'external') {
      return {
        name: data.name,
        domain: data.external_domain,
        scan_type: data.scan_type
      };
    }

    return {
      name: data.name,
      target_path: data.target_path,
      scan_type: data.scan_type,
      scan_location: data.scan_location,
      scan_source: data.scan_source,
      repo_username: data.repo_username,
      repo_token: data.repo_token,
      is_private_repo: data.is_private_repo,
      tenant_id: data.tenant_id
    };
  };

  const handleScanModeChange = (mode) => {
    setFormData((prev) => ({
      ...prev,
      scan_mode: mode,
      scan_type: mode === 'external' ? 'external_discovery' : 'full',
      target_path: '',
      scan_source: 'filesystem',
      repo_username: '',
      repo_token: '',
      is_private_repo: false,
      external_domain: mode === 'external' ? prev.external_domain : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = buildScanPayload(formData);
      const response = formData.scan_mode === 'external'
        ? await discoveryScansAPI.create(payload)
        : await scansAPI.create(payload);
      setFormData({
        name: '',
        scan_mode: 'internal',
        target_path: '',
        scan_type: 'full',
        scan_location: isSentinel ? 'agent' : 'hub',
        scan_source: 'filesystem',
        repo_username: '',
        repo_token: '',
        is_private_repo: false,
        external_domain: ''
      });
      setShowWizard(false);
      
      // Navigate to the appropriate view for the new scan
      if (response.data && response.data.id) {
        if (response.data.tenant_id) {
          navigate(`/msp/tenants/${response.data.tenant_id}/external-scans`);
        } else {
          navigate(`/scans/${response.data.id}`);
        }
      } else {
        loadScans();
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error('Error creating scan:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error creating scan. Please check the target path exists.';
      
      if (errorMessage.includes('Sentinel users must use edge agents')) {
        if (window.confirm(`${errorMessage}\n\nWould you like to go to the Edge Agents page to set one up?`)) {
          navigate('/agents');
        }
      } else {
        alert(errorMessage);
      }
      
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (scanId) => {
    if (window.confirm('Are you sure you want to delete this scan and all its findings?')) {
      try {
        await scansAPI.delete(scanId);
        loadScans(); // Reload the list
      } catch (error) {
        console.error('Error deleting scan:', error);
        alert('Failed to delete scan.');
      }
    }
  };

  const handleWizardComplete = async (wizardData) => {
    setIsSubmitting(true);
    try {
      if (wizardData.frequency === 'once') {
        const payload = buildScanPayload(wizardData);
        const response = wizardData.scan_mode === 'external'
          ? await discoveryScansAPI.create(payload)
          : await scansAPI.create(payload);
          
        if (response.data && response.data.id) {
          if (response.data.tenant_id) {
            navigate(`/msp/tenants/${response.data.tenant_id}/external-scans`);
          } else {
            navigate(`/scans/${response.data.id}`);
          }
        } else {
          loadScans();
        }
      } else {
        // Scheduled Scan
        await scheduledScansAPI.create(wizardData);
        loadScheduledScans();
      }
      setShowWizard(false);
    } catch (error) {
      console.error('Error from wizard submission:', error);
      alert(error.response?.data?.message || error.response?.data?.error || 'Error processing scan request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredScans = scans.filter(scan => {
    if (filters.severity === 'high') return scan.high_severity > 0;
    if (filters.severity === 'medium') return scan.medium_severity > 0;
    if (filters.severity === 'low') return scan.low_severity > 0;
    return true;
  });

  if (loading) {
    return <div className="spinner" />;
  }

  const severityData = stats ? [
    { name: 'High', value: stats.severity_breakdown.high, color: '#f85149' },
    { name: 'Medium', value: stats.severity_breakdown.medium, color: '#d29922' },
    { name: 'Low', value: stats.severity_breakdown.low, color: '#3fb950' },
  ] : [];

  const vulnTypeData = stats ? Object.entries(stats.vulnerability_types)
    .map(([name, value]) => ({
      name: name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
      value
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) : [];

  const calculateAxisHeight = (data, baseHeight = 60, charThreshold = 12) => {
    if (!data || data.length === 0) return baseHeight;
    const maxLength = Math.max(...data.map(item => item.name.length));
    if (maxLength <= charThreshold) return baseHeight;
    const extraHeight = (maxLength - charThreshold) * 7;
    return Math.min(baseHeight + extraHeight, 220);
  };

  const xAxisHeight = calculateAxisHeight(vulnTypeData);

  return (    <>
      <ReturnBanner />    <div className="container theme-github">
      <div className="gh-repo-header" style={{ marginBottom: '20px' }}>
        <div className="gh-repo-title">
          <svg className="gh-icon" viewBox="0 0 16 16" width="16" height="16"><path fillRule="evenodd" d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7L5 12.865a.5.5 0 11-.706.708l-1.611-1.611A2.503 2.503 0 012 10.25V2.5zM4.5 1.5a1 1 0 00-1 1V10.25c0 .41.16.806.44 1.104l.843.843L6.34 10.64a1 1 0 01.78-.39h7.38v-8.75a1 1 0 00-1-1h-9z"></path></svg>
          <span className="repo-owner">Workspace</span>
          <span className="path-divider">/</span>
          <span className="repo-name">Security Scans</span>
        </div>
        <div className="gh-repo-actions">
          <button className="gh-btn gh-btn-primary" onClick={() => setShowWizard(true)}>
            Create New Scan
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="gh-stat-card">
          <div className="stat-label">Total Scans</div>
          <div className="stat-value">{stats?.total_scans || 0}</div>
        </div>
        <div className="gh-stat-card">
          <div className="stat-label">Total Findings</div>
          <div className="stat-value">{stats?.total_findings || 0}</div>
        </div>
        <div className="gh-stat-card gh-stat-high" style={{ cursor: 'pointer' }} onClick={() => setFilters({ ...filters, severity: 'high' })}>
          <div className="stat-label">High Severity</div>
          <div className="stat-value">{stats?.severity_breakdown?.high || 0}</div>
        </div>
        <div className="gh-stat-card gh-stat-medium" style={{ cursor: 'pointer' }} onClick={() => setFilters({ ...filters, severity: 'medium' })}>
          <div className="stat-label">Medium Severity</div>
          <div className="stat-value">{stats?.severity_breakdown?.medium || 0}</div>
        </div>
        <div className="gh-stat-card" style={{ cursor: 'pointer' }} onClick={() => setFilters({ ...filters, severity: 'low' })}>
          <div className="stat-label">Low Severity</div>
          <div className="stat-value">{stats?.severity_breakdown?.low || 0}</div>
        </div>
      </div>

      <div className="charts-grid">
        <div className="gh-panel">
          <div className="gh-panel-header">
            <h3 className="gh-panel-title">Severity Distribution</h3>
          </div>
          <div className="gh-panel-body">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={severityData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="gh-panel">
          <div className="gh-panel-header">
            <h3 className="gh-panel-title">Top Vulnerability Types</h3>
          </div>
          <div className="gh-panel-body">
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={vulnTypeData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(48, 54, 61, 0.5)" />
                <XAxis 
                  dataKey="name" 
                  angle={-45} 
                  textAnchor="end" 
                  height={xAxisHeight} 
                  interval={0}
                  tick={{ fontSize: 10, fill: 'var(--gh-text-muted)' }}
                  stroke="var(--gh-border)"
                />
                <YAxis tick={{ fill: 'var(--gh-text-muted)' }} stroke="var(--gh-border)" />
                <Tooltip 
                  contentStyle={{ backgroundColor: 'var(--gh-bg-secondary)', borderColor: 'var(--gh-border)', color: 'var(--gh-text)' }}
                  itemStyle={{ color: 'var(--gh-text)' }}
                />
                <Bar dataKey="value" fill="#58a6ff" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {isTrial && (
        <div className="gh-panel" style={{ border: '1px solid var(--gh-warning-border)', background: 'rgba(210, 153, 34, 0.1)', marginBottom: '20px' }}>
          <div className="gh-panel-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ margin: 0 }}><strong>Trial Period:</strong> You have 1 free scan remaining. Upgrade to unlock unlimited scans and advanced analysis.</p>
            <button className="gh-btn" onClick={() => navigate('/pricing')}>Upgrade Plan</button>
          </div>
        </div>
      )}

      <ScanWizard 
        isOpen={showWizard} 
        onClose={() => setShowWizard(false)} 
        onComplete={handleWizardComplete}
        isSentinel={isSentinel}
        onBrowse={handleBrowse}
        initialData={{
          ...formData, // Spread existing form data
          target_path: formData.target_path || scheduledFormData.target_path // Ensure we pick up the path from whichever was active
        }}
      />

      {/* Scheduled Scans Section - Only for Overdrive/Nexus */}
      {canScheduleScans && (
        <div className="gh-panel" style={{ marginBottom: '24px' }}>
          <div className="gh-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 className="gh-panel-title">Scanning Automation (CI/CD Parallel)</h3>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--gh-text-muted)' }}>Recurring vulnerability analysis with priority alerts</p>
            </div>
            <button 
              className="gh-btn gh-btn-primary" 
              onClick={() => { 
                setEditingScheduledScan(null); 
                resetScheduledForm(); 
                setScheduledFormData(prev => ({ ...prev, frequency: 'daily' }));
                setShowWizard(true);
              }}
            >
              Establish Automation
            </button>
          </div>

          <div className="gh-panel-body">
            {scheduledScansLoading ? (
              <div className="spinner-small"></div>
            ) : scheduledScans.length === 0 ? (
              <div className="gh-empty-state">
                <p>No automated security cycles established. Set up a schedule to maintain continuous compliance assets.</p>
              </div>
            ) : (
              <div className="gh-table-container">
                <table className="gh-table">
                  <thead>
                    <tr>
                      <th>Automation Instance</th>
                      <th>Cadence</th>
                      <th>Next Deployment</th>
                      <th>Threshold</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledScans.map(scan => (
                      <tr key={scan.id} className="gh-table-row">
                        <td>
                          <div style={{ fontWeight: 600 }}>{scan.name}</div>
                          <div style={{ fontSize: '11px', color: 'var(--gh-text-muted)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{scan.target_path}</div>
                        </td>
                        <td>
                          <div className="gh-badge" style={{ textTransform: 'capitalize' }}>
                            {scan.frequency}
                            {scan.frequency === 'weekly' && ` (${['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][scan.day_of_week]})`}
                            {scan.frequency === 'monthly' && ` (Day ${scan.day_of_month})`}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--gh-text-muted)', marginTop: '4px' }}>{scan.time_of_day} UTC</div>
                        </td>
                        <td style={{ fontSize: '12px' }}>{scan.next_run_at ? new Date(scan.next_run_at).toLocaleString() : 'Pending Queue'}</td>
                        <td>
                          {scan.email_alerts_enabled ? (
                            <span className="gh-badge gh-badge-success">📧 {scan.alert_on_severity}+</span>
                          ) : (
                            <span style={{ color: 'var(--gh-text-muted)', fontSize: '11px' }}>No Alerts</span>
                          )}
                        </td>
                        <td>
                          <span 
                            className={`gh-badge gh-badge-${scan.is_active ? 'success' : 'warning'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleToggleActive(scan)}
                          >
                            {scan.is_active ? 'Active' : 'Paused'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
                            <button onClick={() => handleRunNow(scan.id)} className="gh-btn gh-btn-sm" title="Trigger immediately">Run</button>
                            <button onClick={() => handleEditScheduledScan(scan)} className="gh-btn gh-btn-sm">Modify</button>
                            <button onClick={() => handleDeleteScheduledScan(scan.id)} className="gh-btn gh-btn-danger gh-btn-sm">Purge</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade prompt for Sentinel users */}
      {isSentinel && (
        <div className="gh-panel" style={{ background: 'linear-gradient(135deg, rgba(56, 139, 253, 0.1) 0%, rgba(13, 17, 23, 1) 100%)', border: '1px solid var(--gh-border-active)', marginBottom: '24px' }}>
          <div className="gh-panel-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', textAlign: 'center', padding: '40px' }}>
            <span style={{ fontSize: '48px' }}>🔄</span>
            <div>
              <h3 className="gh-panel-title" style={{ fontSize: '20px', marginBottom: '8px' }}>Scheduled Scans with Email Alerts</h3>
              <p style={{ margin: 0, color: 'var(--gh-text-muted)', maxWidth: '500px' }}>
                Upgrade to Overdrive or Nexus to set up recurring vulnerability scans with automatic email notifications when issues are found.
              </p>
            </div>
            <button className="gh-btn gh-btn-primary" onClick={() => navigate('/pricing')}>
              View Upgrade Options
            </button>
          </div>
        </div>
      )}

      <div className="gh-panel" style={{ marginTop: '24px' }}>
        <div className="gh-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 className="gh-panel-title">Repository Scan History</h3>
          <div className="gh-filters">
            <select
              className="gh-select"
              style={{ width: '180px' }}
              value={filters.severity}
              onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            >
              <option value="">Sort by Severity</option>
              <option value="high">High Risk Findings</option>
              <option value="medium">Medium Risk Findings</option>
              <option value="low">Low Risk Findings</option>
            </select>
          </div>
        </div>
        <div className="gh-panel-body">
          {scans.length === 0 ? (
            <div className="gh-empty-state">
              <p>No scans found. Start by creating your first project scan above.</p>
            </div>
          ) : filteredScans.length === 0 ? (
            <div className="gh-empty-state">
              <p>No scans match your criteria.</p>
            </div>
          ) : (
            <div className="gh-table-container">
              <table className="gh-table">
                <thead>
                  <tr>
                    <th>Scan Registry</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Findings</th>
                    <th>Executed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredScans.map(scan => (
                    <tr key={scan.id} className="gh-table-row">
                      <td>
                        <div style={{ fontWeight: 600, color: 'var(--gh-link)' }}>
                          <Link to={`/scans/${scan.id}`}>{scan.name}</Link>
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--gh-text-muted)' }}>{scan.target_path}</div>
                      </td>
                      <td>
                        <span className={`gh-badge gh-badge-${scan.status === 'completed' ? 'success' : scan.status === 'failed' ? 'danger' : 'warning'}`}>
                          {scan.status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                          <span className="badge badge-high">{scan.high_severity}</span>
                          <span className="badge badge-medium">{scan.medium_severity}</span>
                          <span className="badge badge-low">{scan.low_severity}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: '12px' }}>{new Date(scan.created_at).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <Link to={`/scans/${scan.id}`} className="gh-btn gh-btn-sm">Report</Link>
                          <button 
                            onClick={() => handleDelete(scan.id)} 
                            className="gh-btn gh-btn-danger gh-btn-sm"
                          >
                            Purge
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showPicker && (
        <div className="modal-overlay">
          <div className="modal-content folder-picker">
            <button 
              className="picker-close-btn" 
              onClick={() => setShowPicker(false)}
              aria-label="Close file explorer"
            >
              ×
            </button>
            <div className="picker-header">
              <h3>Select Target Directory {pickerItems.is_agent ? '(Edge Agent)' : '(Hub)'}</h3>
              {!isSentinel && pickerMode !== 'scheduled_scan' && (
                <div className="picker-toggle">
                  <button 
                    className={`btn-toggle ${browseTarget === 'hub' ? 'active' : ''}`}
                    onClick={() => loadPickerData('/app/projects', 'hub')}
                  >
                    HUB_FS
                  </button>
                  <button 
                    className={`btn-toggle ${browseTarget === 'agent' ? 'active' : ''}`}
                    onClick={() => loadPickerData('/app/projects', 'agent')}
                  >
                    AGENT_FS
                  </button>
                </div>
              )}
            </div>
            <div className="picker-path">
              <strong>Current:</strong> {pickerItems.current}
            </div>
            <div className="picker-list">
              {pickerItems.parent && (
                <div className="picker-item parent" onClick={goUp}>
                  📁 .. (Parent Directory)
                </div>
              )}
              {pickerLoading ? (
                <div className="spinner-small"></div>
              ) : (
                <>
                  {pickerItems.directories && pickerItems.directories.length > 0 && (
                    <>
                      {pickerItems.directories.map(dir => (
                        <div key={dir} className="picker-item picker-directory" onClick={() => selectFolder(dir)}>
                          📁 {dir}
                        </div>
                      ))}
                    </>
                  )}
                  {pickerItems.files && pickerItems.files.length > 0 && (
                    <>
                      {pickerItems.files.map(file => (
                        <div key={file} className="picker-item picker-file">
                          📄 {file}
                        </div>
                      ))}
                    </>
                  )}
                  {!pickerLoading && (!pickerItems.directories || pickerItems.directories.length === 0) && (!pickerItems.files || pickerItems.files.length === 0) && (
                    <div className="picker-empty">No files or directories found</div>
                  )}
                </>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowPicker(false)}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={confirmSelection}>Select This Folder</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default Scans;
